/// <reference types="vite-plugin-svgr/client" />
import React, { createContext, useContext, useState, useRef, useEffect, ReactNode, useCallback } from "react";
import { createPortal } from "react-dom";
import CloseIcon from "./close.svg?react";
import { useTranslation } from "react-i18next";
import { IconButton } from "../IconButton";

export const enum DrawWidth {
    Smaller = "445px",
    Small = "682px",
    Large = "90vw",
}

export type DrawerLocation = "right" | "left";

export type DrawTitle = string | React.ReactNode | undefined;

interface DrawerContextType {
    isOpen: boolean;
    title: DrawTitle;
    titleIconTrailing?: ReactNode;
    drawerContent: ReactNode;
    width: DrawWidth;
    openDrawer: (
        title: DrawTitle,
        content: ReactNode,
        width?: DrawWidth,
        titleIconTrailing?: ReactNode,
        onClose?: () => void,
    ) => void;
    setTitle: React.Dispatch<React.SetStateAction<DrawTitle>>;
    setWidth: React.Dispatch<React.SetStateAction<DrawWidth>>;
    closeDrawer: () => void;
    drawerRef: React.RefObject<HTMLElement>;
}

const DrawerContext = createContext<DrawerContextType | null>(null);

interface DrawerProviderProps {
    children: ReactNode;
}

export const DrawerProvider: React.FC<DrawerProviderProps> = ({ children }) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [width, setWidth] = useState<DrawWidth>(DrawWidth.Small);
    const [title, setTitle] = useState<string | React.ReactNode | undefined>(undefined);
    const [titleIconTrailing, setTitleIconTrailing] = useState<ReactNode>(null);
    const [drawerContent, setDrawerContent] = useState<ReactNode>(null);
    const [onClose, setOnClose] = useState<(() => void) | undefined>(undefined);
    const drawerRef = useRef<HTMLElement>(null);

    const openDrawer = (
        title: string | React.ReactNode | undefined,
        content: ReactNode,
        width: DrawWidth = DrawWidth.Small,
        titleIconTrailing?: ReactNode,
        onClose?: () => void,
    ) => {
        setDrawerContent(content);
        setWidth(width);
        setTitle(title);
        setTitleIconTrailing(titleIconTrailing ?? null);
        setIsOpen(true);
        setOnClose(() => {
            return onClose;
        });
    };

    const closeDrawer = useCallback(() => {
        setIsOpen(false);
        // Clear content after transition for smoother unmount
        setTimeout(() => {
            setDrawerContent(null);
        }, 300);
        onClose?.();
    }, [onClose]);

    // Close drawer on Escape key press
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape" && isOpen) {
                closeDrawer();
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [isOpen, closeDrawer]);

    const value: DrawerContextType = {
        isOpen,
        title,
        drawerContent,
        width,
        openDrawer,
        closeDrawer,
        setTitle,
        setWidth,
        drawerRef,
        titleIconTrailing,
    };

    return <DrawerContext.Provider value={value}>{children}</DrawerContext.Provider>;
};

export const useDrawer = () => {
    const context = useContext(DrawerContext);
    if (!context) {
        throw new Error("useDrawer must be used within a DrawerProvider");
    }
    return context;
};

const headerStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "1rem",
    position: "relative",
};

const titleContainerStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "8px",
};

const titleStyle: React.CSSProperties = {
    fontSize: "1.25rem",
    fontWeight: "600",
    color: "#1a202c",
};

const contentAreaStyle: React.CSSProperties = {
    flexGrow: 1,
    padding: "20px 16px 0 16px",
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
};

const Drawer: React.FC<{ location?: DrawerLocation }> = ({ location = "right" }) => {
    const { isOpen, title, titleIconTrailing, drawerContent, width, drawerRef, closeDrawer } = useDrawer();
    const { t } = useTranslation();

    const drawerPanelStyle: React.CSSProperties = {
        position: "absolute",
        top: 0,
        bottom: 0,
        right: location == "right" ? 0 : undefined,
        left: location == "left" ? 0 : undefined,
        height: "100%",
        transition: "transform 200ms ease-in",
        transform: isOpen
            ? location == "right"
                ? "translateX(0)"
                : "translateX(0)"
            : location == "right"
                ? `translateX(${width})`
                : `translateX(-${width})`,
    };

    return createPortal(
        <div
            style={{
                position: "fixed",
                inset: 0,
                zIndex: 1,
                overflow: "hidden",
                pointerEvents: isOpen ? "auto" : "none",
            }}
        >
            {/* Overlay */}
            <div
                style={{
                    position: "absolute",
                    inset: 0,
                    backgroundColor: isOpen ? "rgba(31, 41, 55)" : "rgba(31, 41, 55)",
                    opacity: isOpen ? 0.75 : 0,
                    transition: "opacity 150ms ease-out",
                }}
                aria-hidden="true"
                onClick={isOpen ? closeDrawer : undefined}
            />

            {/* Drawer Panel */}
            <section
                style={drawerPanelStyle}
                aria-labelledby="drawer-title"
                role="dialog"
                aria-modal="true"
                ref={drawerRef}
            >
                <div
                    style={{
                        width: "100vw",
                        maxWidth: width,
                        backgroundColor: "white",
                        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                    }}
                >
                    <div style={headerStyle}>
                        <div style={titleContainerStyle}>
                            <h2 id="drawer-title" style={titleStyle}>
                                {typeof title === "string" ? t(title) : title}
                            </h2>
                            {titleIconTrailing}
                        </div>
                        <IconButton tabIndex={isOpen ? 1 : -1} icon={<CloseIcon />} onClick={closeDrawer} />
                    </div>
                    <div style={contentAreaStyle}>{drawerContent}</div>
                </div>
            </section>
        </div>,
        document.body,
    );
};

export { Drawer };
